import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation
} from "@angular/core";
import {ApiService} from "../../services/api/api.service";
import {ConstantProperty} from '../../_models/constant-property';
import {ComponentType} from "../../_models/component-type";
import {EventType} from "../../_models/event-type";
import {
    FieldMap,
    Option,
    QueryBuilderConfig,
    QueryBuilderConfigType,
    Rule,
    RuleSet
} from '../../_typing/query-builder';
import {WireQueryBuilderComponent} from "../../components/wire-query-builder/wire-query-builder.component";
import {CustomEventsService} from "../../services/custom-events.service";
import {concatMap, first, map, takeUntil, tap} from 'rxjs/operators';
import {forkJoin, Subject} from "rxjs";
import {COMPONENT_EVENTS_CONFIG} from "../../_typing/config/component-events-table";
import {EventConfigColumn} from "../../_typing/config/config-column";
import {deepCopy, sortObjectsByFlatProperty} from '../../lib/utils';
import {IDMap, KeyMap, ModelID} from '../../_typing/generic-types';
import {COMMON_FORM_IMPORTS} from "../../shared/import-groups/Common";
import {CommonModule} from "@angular/common";
import {ComponentTypeDataService} from "../../data/component-type-data.service";
import {ConstantPropertyDataService} from "../../data/constant-property-data.service";
import {getColIds} from "../../tables/custom-events-table/utils";
import {SelectManySearchBaseComponent} from "../../components/select-search-api/many/select-many-search-base.component";

@Component({
    selector: 'query-builder-form',
    templateUrl: 'query-builder-form.component.html',
    styleUrls: ['./query-builder-form.component.less'],
    imports: [
        WireQueryBuilderComponent,
        COMMON_FORM_IMPORTS,
        CommonModule,
        SelectManySearchBaseComponent
    ],
    encapsulation: ViewEncapsulation.None
})
export class QueryBuilderFormComponent implements OnInit, OnDestroy {

    @Input() tileConfig: QueryBuilderConfigType;
    @Input() content: string;
    @Input() modelTypeId: string;

    @Input() query: RuleSet | null;
    @Output() queryChange: EventEmitter<RuleSet | null> = new EventEmitter<RuleSet | null>();

    private readonly onDestroy = new Subject<void>();
    @Input() filter_cp_dict: { [key: string]: ConstantProperty };
    @Input() filter_constant_properties: ConstantProperty[] = [];
    @Input() filter_component_type: ComponentType;
    @Input() filter_event_types: EventType[];
    @Input() filter_event_type_component_types: ComponentType[];
    @Input() filter_related_component_types: ComponentType[];
    @Input() isFilterBuilder: Boolean = false;
    @Input() selected_cols_dict: KeyMap<EventConfigColumn>;
    cp_dict: { [key: string]: ConstantProperty };
    constant_properties: ConstantProperty[] = [];
    component_types: ComponentType[] = [];
    event_type_component_types: ComponentType[] = [];
    event_types: EventType[] = [];
    ordered_column_options: string[];
    public config_ready = false;

    // Searchable attributes on a Component, alongside Constant Properties
    get defaultComponentTypeFields() {
        return {
            ...(!this.isFilterBuilder || this.selected_cols_dict?.name) && ({name: {name: 'Name', type: 'string'}}),
            ...(!this.isFilterBuilder || this.selected_cols_dict?.start_time) && ({
                start_time: {
                    name: 'Start Time',
                    type: 'datetime'
                }
            }),
            ...(!this.isFilterBuilder || this.selected_cols_dict?.end_time) && ({
                end_time: {
                    name: 'End Time',
                    type: 'datetime'
                }
            }),
            ...(!this.isFilterBuilder || this.selected_cols_dict?.created_on) && ({
                created_on: {
                    name: 'Created On',
                    type: 'datetime'
                }
            }),
            ...this.userAuditFields
        };
    }

    // Searchable attributes on an Event, alongside Constant Properties
    get defaultEventTypeFields() {
        return {
            ...(!this.isFilterBuilder || this.selected_cols_dict?.start) && ({
                start: {
                    name: 'Start Time',
                    type: 'datetime'
                }
            }),
            ...(!this.isFilterBuilder || this.selected_cols_dict?.end) && ({
                end: {
                    name: 'End Time',
                    type: 'datetime'
                }
            }),
            ...(!this.isFilterBuilder || this.selected_cols_dict?.created_on) && ({
                created_on: {
                    name: 'Created On',
                    type: 'datetime'
                }
            }),
            ...this.userAuditFields
        };
    }

    get userAuditFields() {
        //TODO make these and start, end etc constants, update event table configs to use the correct backend keys
        const changedBy: EventConfigColumn = this.selected_cols_dict?.['changed by'] || this.selected_cols_dict?.['changed_by'];
        return {
            ...(!this.isFilterBuilder || changedBy) &&
            ({changed_by: {name: changedBy?.title || 'Changed By', type: 'user'}})
        };
    }

    // Initial state for the form builder
    defaultQuery = {
        condition: 'and',
        rules: []
    };

    baseConfig: QueryBuilderConfig = {
        fields: {}
    };

    config: QueryBuilderConfigType;

    selectedRelatedComponentTypes: ComponentType[];
    componentRelationshipFilters: any[];
    propertiesLoadingDict: IDMap<boolean> = {};
    componentTypePropertyDict: IDMap<ConstantProperty[]> = {};
    selectedComponentTypePropertyDict: IDMap<ModelID[]> = {};
    additionalFields: FieldMap = {};

    constructor(private api: ApiService,
                public customEventsService: CustomEventsService,
                private componentTypeData: ComponentTypeDataService,
                private propertyData: ConstantPropertyDataService) {
    }

    ngOnInit(): void {
        if (this.isFilterBuilder) {
            this.component_types = [this.filter_component_type];
            this.cp_dict = this.filter_cp_dict;
            this.constant_properties = this.filter_constant_properties.filter(cp => getColIds(Object.values(this.selected_cols_dict)).includes(cp.id));
            this.event_type_component_types = this.filter_event_type_component_types;
            this.ordered_column_options = sortObjectsByFlatProperty(Object.values(this.selected_cols_dict), 'index')
                .map(c => this.mapToFieldFormat(c));
            let fields = {
                ...(this.filter_component_type ? this.defaultComponentTypeFields : this.defaultEventTypeFields),
                ...this.fieldsFromConstantProperty(this.constant_properties), ...this.fieldsFromComponentType(this.filter_related_component_types),
                ...this._getSelectedRelatedPropertiesFromColumns()
            };
            //TODO component_type filters not working here and event_types are not available on component_events table
            //so not sure if adding to fields is actually necessary here.
            if (this.filter_event_types) {
                this.event_types = this.filter_event_types;
                fields = Object.assign(fields, this.fieldsFromComponentType(this.event_type_component_types));
            } else {
                this.customEventsService.getComponentTypeEventTypes([this.filter_component_type.id])
                    .pipe(map(res => {
                        this.event_types = res.data;
                        fields = Object.assign(fields, this.fieldsFromEventType(this.event_types));
                    }));
            }
            if (this.selected_cols_dict?.name?.title) {
                fields['name'].name = this.selected_cols_dict.name.title;
            }

            this.config = {...this.baseConfig, fields};
            this.config_ready = true;
        } else {
            if ((this.content === 'component-events-table' || this.content === 'component') && this.modelTypeId) {
                this.componentRelationshipFilters = this.componentTypeData.getComponentRelationshipFilters(this.modelTypeId);

                if (this.content === 'component-events-table') {
                    // Pass raw COMPONENT_EVENTS_CONFIG — WQB's services will load fields
                    this.config = this.tileConfig;
                } else {
                    // Pivot 'component' — construct minimal compatible config for WQB services
                    this.config = {
                        selected_component_type: {id: this.modelTypeId},
                        selected_cols: {component_type: [], event: [], component: {}}
                    } as COMPONENT_EVENTS_CONFIG;
                }

                this._loadInitialRelatedProperties();
                this.config_ready = true;
            } else if ((this.content === 'custom-events-table' || this.content === 'event') && this.modelTypeId) {
                if (this.content === 'custom-events-table') {
                    // Pass raw CustomEventsConfig — WQB's services will load fields
                    this.config = this.tileConfig;
                } else {
                    // Pivot 'event' — construct minimal compatible config for WQB services
                    this.config = {columns: [], event_types: []} as any;
                }
                this.config_ready = true;
            }
        }
    }

    loadRelatedProperties($event: ComponentType[]) {
        this.selectedRelatedComponentTypes = $event;
        const componentTypes = $event?.filter(ct => !this.componentTypePropertyDict[ct.id] && !this.propertiesLoadingDict[ct.id]);
        const componentTypeIds = componentTypes.map(ct => ct.id);

        if (!componentTypeIds?.length) return;
        componentTypeIds.forEach(ctId => this.propertiesLoadingDict[ctId] = true);

        this.propertyData.getConstantPropertiesByRelationshipIds(componentTypeIds, 'component_types').pipe(
            tap(response => {
                response?.data?.forEach(property => {
                    let componentTypeIds = property.relationships.component_types.data?.map(ct => ct.id);
                    componentTypeIds.forEach(componentTypeId => {
                        this.componentTypePropertyDict[componentTypeId] = (this.componentTypePropertyDict[componentTypeId] || []).concat([property]);
                    });
                });
                componentTypes.forEach(componentType => {
                    const fields = this.fieldsFromConstantProperty(this.componentTypePropertyDict[componentType.id], `component@${componentType.id}@constant_property@`, `${componentType.attributes.name}:`);
                    this.additionalFields = {...this.additionalFields, ...fields};
                    this.propertiesLoadingDict[componentType.id] = false;
                });
            }),
            first(),
            takeUntil(this.onDestroy)
        ).subscribe();
    }

    /**
     * Scans the existing query for references to related component type properties,
     * loads them, and sets them as additionalFields for WQB to merge.
     */
    private _loadInitialRelatedProperties(): void {
        if (!this.query) return;
        this._matchSelected(this.query);

        if (!Object.keys(this.selectedComponentTypePropertyDict)?.length) return;
        let $obs: any[] = [];
        let fields = {};

        Object.keys(this.selectedComponentTypePropertyDict).forEach(componentTypeId => {
            let propIds = this.selectedComponentTypePropertyDict[componentTypeId]?.filter(p => p !== 'name');

            let obs = this.componentTypeData.getComponentType(componentTypeId).pipe(
                concatMap(response => {
                    const componentType = response.data;
                    return this.propertyData.getConstantProperties(propIds).pipe(map((properties) => {
                        let propFields = this.fieldsFromConstantProperty(properties.data, `component@${componentTypeId}@constant_property@`, `${componentType.attributes.name}:`);
                        fields = {...fields, ...propFields};
                    }));
                })
            );

            $obs.push(obs);
        });

        if (!$obs.length) return;

        forkJoin($obs).pipe(first(), takeUntil(this.onDestroy)).subscribe(() => {
            this.additionalFields = {...this.additionalFields, ...fields};
        });
    }

    private _getComponentTypeNameField(componentType: ComponentType) {
        return {
            name: `${componentType.attributes.name}:Name`,
            type: 'string',
            value: `[component@${componentType.id}@name]`
        };
    }

    fieldsFromConstantProperty(constant_properties: ConstantProperty[], prefix = 'constant_property@', namePrefix = ''): FieldMap {
        return constant_properties.reduce((fields, cp) => {
            if (cp.attributes.data_type === 'file') {
                return fields;
            }
            let type: 'dropdown' | 'text' | 'number' | string = cp.attributes.data_type; // Add all allowed types
            let options: Option[] = [];
            let isUserField: boolean = false;
            let isUserGroupField: boolean = false;
            let isAccountField: boolean = false;

            if (type === 'float') {
                type = 'number';
            } else if (type === 'user' || (this.isFilterBuilder && cp.type === 'user')) {
                type = 'user';
                isUserField = true;
            } else if (cp.attributes.is_drop_down_list) {
                type = 'dropdown';
                options = cp.attributes.json.map(option => ({
                    name: option,
                    value: option
                }));
            }

            fields[`[${prefix}${cp.id}]`] = {
                name: (this.selected_cols_dict?.[cp.id]?.title || `${namePrefix}${cp.attributes.name}`),
                type,
                ...(type === 'dropdown' && {options: options}),
                isUserField,
                isUserGroupField,
                isAccountField,
            };

            return fields;
        }, {});
    }

    fieldsFromComponentType(component_types: ComponentType[]): FieldMap {
        return component_types.reduce((fields, ct) => {
            fields[`[component@${ct.id}@name]`] = this._getComponentTypeNameField(ct);
            fields[`[component_type@${ct.id}]`] = {name: ct.attributes.name, type: 'boolean'};
            return fields;
        }, {})
    }

    fieldsFromEventType(event_types: EventType[]): FieldMap {
        return event_types.reduce((fields, et) => {
            fields[`[event_type@${et.id}]`] = {name: et.attributes.name, type: 'boolean'};
            return fields;
        }, {})
    }

    private _getSelectedRelatedPropertiesFromColumns(): FieldMap {
        let fields = {};
        const componentTypeIds = Object.values(this.selected_cols_dict).filter(col => col.related_type_id)?.map(col => col.related_type_id);
        if (!componentTypeIds?.length) return fields;

        componentTypeIds.forEach(ctId => {
            const componentType = this.filter_related_component_types?.find(ct => ct.id === ctId);
            const propIds = Object.values(this.selected_cols_dict).filter(col => col.related_type_id === ctId)?.map(col => col.id);
            const properties = this.filter_constant_properties.filter(cp => propIds.includes(cp.id));
            let propFields = (componentType && properties?.length) ? this.fieldsFromConstantProperty(properties, `component@${componentType.id}@constant_property@`, `${componentType.attributes.name}:`) : {};
            fields = {...fields, ...propFields};
        })
        return fields
    }

    private mapToFieldFormat(column: EventConfigColumn): string {
        if (column.type === 'attribute') {
            return column.id.toLowerCase();
        }
        return `[${column.type}@${column.id}]`;
    }

    private _matchSelected(query: RuleSet) {
        const regex = /^\[(component|event)@([a-f0-9-]+)@(\w+)@([a-f0-9-]+)\]$/;
        deepCopy(query).rules.map((rule: RuleSet | Rule) => {
            if ('field' in rule) {
                if (!rule.field.endsWith('@name')) {
                    let cpMatch = rule.field.match(regex);
                    if (cpMatch?.length > 4 && cpMatch[1] === 'component') {
                        const ctId = cpMatch[2];
                        const cpId = cpMatch[4];
                        this.selectedComponentTypePropertyDict[ctId] = (this.selectedComponentTypePropertyDict[ctId] || []).concat([cpId])
                    }
                }

            } else if ('condition' in rule) {
                return this._matchSelected(rule);
            }
        })
    }

    onQueryChange(query: RuleSet): void {
        this.query = query;
        this.queryChange.emit(query);
    }

    isRelatedComponentTypeDisabled(item) {
        return this.componentTypePropertyDict?.[item.id] || this.propertiesLoadingDict?.[item.id];
    }

    ngOnDestroy(): void {
        this.onDestroy.next();
        this.onDestroy.unsubscribe();
    }
}
